/**
 * WizardStepImages
 *
 * Step 2 of the launchpad creation wizard.
 * Handles thumbnail (required for validation) and banner (optional) image uploads.
 * Supports banner removal and vertical drag-to-reposition.
 */

import React, { useRef, useState, useCallback } from 'react';
import { FeatherUpload, FeatherImage, FeatherCheck } from '@subframe/core';
import { Button } from '../../../ui/components/Button';
import { uploadLaunchThumbnail, uploadLaunchBanner, deleteLaunchBanner, updateBannerPosition } from '../../../api/launches';


export interface WizardStepImagesProps {
  launchId: string;
  thumbnailUrl: string | null;
  bannerUrl: string | null;
  bannerPositionY?: number;
  onUploadComplete: (updates: { thumbnailUrl?: string; bannerUrl?: string; bannerPositionY?: number }) => void;
  onError?: (error: string) => void;
}

export const WizardStepImages: React.FC<WizardStepImagesProps> = ({
  launchId,
  thumbnailUrl,
  bannerUrl,
  bannerPositionY = 50,
  onUploadComplete,
  onError,
}) => {
  const thumbnailInputRef = useRef<HTMLInputElement>(null);
  const bannerInputRef = useRef<HTMLInputElement>(null);
  const [uploadingThumbnail, setUploadingThumbnail] = useState(false);
  const [uploadingBanner, setUploadingBanner] = useState(false);
  const [removingBanner, setRemovingBanner] = useState(false);

  // Drag-to-reposition state
  const [isDragging, setIsDragging] = useState(false);
  const [localPositionY, setLocalPositionY] = useState(bannerPositionY);
  const containerRef = useRef<HTMLDivElement>(null);
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Sync local position when prop changes (e.g. after upload)
  const lastPropPositionRef = useRef(bannerPositionY);
  if (bannerPositionY !== lastPropPositionRef.current) {
    lastPropPositionRef.current = bannerPositionY;
    setLocalPositionY(bannerPositionY);
  }

  const handleThumbnailUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setUploadingThumbnail(true);
      const result = await uploadLaunchThumbnail(launchId, file);
      onUploadComplete({ thumbnailUrl: result.thumbnail_url });
    } catch (err) {
      console.error('Failed to upload thumbnail:', err);
      onError?.(err instanceof Error ? err.message : 'Failed to upload thumbnail');
    } finally {
      setUploadingThumbnail(false);
      if (thumbnailInputRef.current) {
        thumbnailInputRef.current.value = '';
      }
    }
  };

  const handleBannerUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setUploadingBanner(true);
      const result = await uploadLaunchBanner(launchId, file);
      setLocalPositionY(50);
      onUploadComplete({ bannerUrl: result.banner_url, bannerPositionY: 50 });
    } catch (err) {
      console.error('Failed to upload banner:', err);
      onError?.(err instanceof Error ? err.message : 'Failed to upload banner');
    } finally {
      setUploadingBanner(false);
      if (bannerInputRef.current) {
        bannerInputRef.current.value = '';
      }
    }
  };

  const handleBannerRemove = async () => {
    try {
      setRemovingBanner(true);
      await deleteLaunchBanner(launchId);
      setLocalPositionY(50);
      onUploadComplete({ bannerUrl: '', bannerPositionY: 50 });
    } catch (err) {
      console.error('Failed to remove banner:', err);
      onError?.(err instanceof Error ? err.message : 'Failed to remove banner');
    } finally {
      setRemovingBanner(false);
    }
  };

  const savePosition = useCallback((posY: number) => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    saveTimeoutRef.current = setTimeout(async () => {
      try {
        await updateBannerPosition(launchId, Math.round(posY));
        onUploadComplete({ bannerPositionY: Math.round(posY) });
      } catch (err) {
        console.error('Failed to save banner position:', err);
      }
    }, 300);
  }, [launchId, onUploadComplete]);

  // Mouse drag handler
  const onMouseDownSimple = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    const startY = e.clientY;
    const startPos = localPositionY;

    const onMouseMove = (ev: MouseEvent) => {
      if (!containerRef.current) return;
      const containerHeight = containerRef.current.getBoundingClientRect().height;
      const deltaY = ev.clientY - startY;
      const deltaPercent = (deltaY / containerHeight) * -100;
      const newPosition = Math.min(100, Math.max(0, startPos + deltaPercent));
      setLocalPositionY(newPosition);
    };

    const onMouseUp = () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
      setIsDragging(false);
      // Read current position from ref-like approach
      setLocalPositionY(prev => {
        savePosition(prev);
        return prev;
      });
    };

    setIsDragging(true);
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
  }, [localPositionY, savePosition]);

  // Touch handlers
  const onTouchStart = useCallback((e: React.TouchEvent) => {
    const touch = e.touches[0];
    const startY = touch.clientY;
    const startPos = localPositionY;

    const onTouchMove = (ev: TouchEvent) => {
      if (!containerRef.current) return;
      const t = ev.touches[0];
      const containerHeight = containerRef.current.getBoundingClientRect().height;
      const deltaY = t.clientY - startY;
      const deltaPercent = (deltaY / containerHeight) * -100;
      const newPosition = Math.min(100, Math.max(0, startPos + deltaPercent));
      setLocalPositionY(newPosition);
    };

    const onTouchEnd = () => {
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('touchend', onTouchEnd);
      setIsDragging(false);
      setLocalPositionY(prev => {
        savePosition(prev);
        return prev;
      });
    };

    setIsDragging(true);
    window.addEventListener('touchmove', onTouchMove, { passive: true });
    window.addEventListener('touchend', onTouchEnd);
  }, [localPositionY, savePosition]);

  return (
    <div className="space-y-8">
      {/* Instructions */}
      <div className="p-4 rounded-lg bg-neutral-100 border border-neutral-border">
        <p className="text-body font-body text-default-font">
          Upload images that represent your collection. The thumbnail is required and will be shown on listings and search results.
        </p>
      </div>

      {/* Thumbnail Upload */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <h3 className="text-body-bold font-body-bold text-default-font">
            Thumbnail
          </h3>
          <span className="text-caption font-caption text-error-600">*Required</span>
          {thumbnailUrl && (
            <span className="flex items-center gap-1 text-caption font-caption text-success-600">
              <FeatherCheck className="w-3 h-3" />
              Uploaded
            </span>
          )}
        </div>
        <p className="text-caption font-caption text-subtext-color mb-4">
          Square image (recommended 400x400 or larger). JPG, PNG, WebP, or GIF.
        </p>

        <div className="flex items-start gap-6">
          {/* Preview */}
          <div className="relative w-32 h-32 bg-neutral-100 dark:bg-neutral-800 rounded-lg overflow-hidden flex-shrink-0">
            {thumbnailUrl ? (
              <img
                src={thumbnailUrl}
                alt="Thumbnail preview"
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <FeatherImage className="w-8 h-8 text-neutral-400" />
              </div>
            )}
          </div>

          {/* Upload Button */}
          <div className="flex flex-col gap-3">
            <Button
              variant="neutral-secondary"
              onClick={() => thumbnailInputRef.current?.click()}
              disabled={uploadingThumbnail}
              loading={uploadingThumbnail}
              icon={<FeatherUpload />}
            >
              {thumbnailUrl ? 'Replace Thumbnail' : 'Upload Thumbnail'}
            </Button>
            <input
              ref={thumbnailInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif"
              className="hidden"
              onChange={handleThumbnailUpload}
            />
          </div>
        </div>
      </div>

      {/* Banner Upload */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <h3 className="text-body-bold font-body-bold text-default-font">
            Banner
          </h3>
          <span className="text-caption font-caption text-subtext-color">(Optional)</span>
          {bannerUrl && (
            <span className="flex items-center gap-1 text-caption font-caption text-success-600">
              <FeatherCheck className="w-3 h-3" />
              Uploaded
            </span>
          )}
        </div>
        <p className="text-caption font-caption text-subtext-color mb-4">
          Wide image for your collection page header (recommended 1200x400 or larger). JPG, PNG, WebP, or GIF.
        </p>

        {/* Preview */}
        <div
          ref={containerRef}
          className={`relative h-40 bg-neutral-100 dark:bg-neutral-800 rounded-lg overflow-hidden mb-4 ${
            bannerUrl ? 'group' : ''
          }`}
        >
          {bannerUrl ? (
            <>
              <img
                src={bannerUrl}
                alt="Banner preview"
                className="w-full h-full object-cover select-none"
                style={{ objectPosition: `center ${localPositionY}%` }}
                draggable={false}
                onMouseDown={onMouseDownSimple}
                onTouchStart={onTouchStart}
              />
              {/* Drag hint overlay */}
              {!isDragging && (
                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                  <span className="bg-black/60 text-white text-caption font-caption px-3 py-1.5 rounded-full">
                    Drag to reposition
                  </span>
                </div>
              )}
              {isDragging && (
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <span className="bg-black/60 text-white text-caption font-caption px-3 py-1.5 rounded-full">
                    Release to save
                  </span>
                </div>
              )}
            </>
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <FeatherImage className="w-12 h-12 text-neutral-300" />
            </div>
          )}
        </div>

        {/* Buttons */}
        <div className="flex gap-2">
          <Button
            variant="neutral-secondary"
            onClick={() => bannerInputRef.current?.click()}
            disabled={uploadingBanner || removingBanner}
            loading={uploadingBanner}
            icon={<FeatherUpload />}
          >
            {bannerUrl ? 'Replace Banner' : 'Upload Banner'}
          </Button>
          {bannerUrl && (
            <Button
              variant="destructive-secondary"
              onClick={handleBannerRemove}
              disabled={uploadingBanner || removingBanner}
              loading={removingBanner}
            >
              Remove
            </Button>
          )}
        </div>
        <input
          ref={bannerInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/gif"
          className="hidden"
          onChange={handleBannerUpload}
        />
      </div>
    </div>
  );
};
